import { useQuery } from "@apollo/client";
import { Add, Search } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import { partition, some } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  OptOutListsQuery,
  SubaccountOptOutSummaryQuery,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import OptOutSearchDialog from "~frontend/components/optouts/OptOutSearchDialog";
import { GET_OPT_OUT_LISTS } from "~frontend/graphql/optOuts";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { SubaccountAdminConfigContextValue } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import usePrevious from "~frontend/util/usePrevious";

import { InternalLink, LinkButton } from "../Link";
import { PageHeader } from "../PageHeader";
import { DeletedOptOutListTable } from "./DeletedOptOutListTable";
import {
  NewAdminList,
  NewOptOutListPrepopulatedSettings,
  OptOutListType,
} from "./NewAdminList";
import { List, OptOutListTable } from "./OptOutListTable";
import { OptOutSharing } from "./OptOutSharing";
import { useOptOutSummary } from "./useOptOutSummary";

type SubaccountOptOutSummary =
  SubaccountOptOutSummaryQuery["subaccountOptOutSummary"];

export const OptOutListPage = ({
  subaccount,
  subaccountLink,
}: {
  subaccount?: SubaccountAdminConfigContextValue;
  subaccountLink?: (path: string) => string;
}): ReactElement => {
  const tenant = useTenant()!;
  const isGlobal = !subaccount;
  useBreadcrumbs([
    {
      label: isGlobal ? "Organization Opt Outs" : "Opt Outs and Strike Lists",
      path: isGlobal ? "/organization/opt-outs" : subaccountLink!("/opt-outs"),
    },
  ]);

  const {
    loading,
    error,
    summary,
    refetch,
    deleteList,
    refreshCounts,
    refreshingCounts,
  } = useOptOutSummary({
    subaccountId: subaccount?.id,
  });

  const loadingListIds = (summary?.optOutLists ?? [])
    .filter(
      list =>
        list.importJob?.status !== "FAILED" && list.importJob?.status !== "DONE"
    )
    .map(list => list.id);

  useQuery<OptOutListsQuery>(GET_OPT_OUT_LISTS, {
    variables: {
      input: {
        subaccountId: subaccount?.id,
        listIds: loadingListIds,
      },
    },
    skip: loadingListIds.length === 0,
    pollInterval: 1000,
    onCompleted: data => {
      if (some(data.optOutLists, list => list.importJob?.status === "DONE")) {
        void refetch();
      }
    },
  });

  const [showNewListDialog, setShowNewListDialog] = useState(false);
  const [newListDefaultSettings, setNewListDefaultSettings] =
    useState<NewOptOutListPrepopulatedSettings>({});

  const hideNewListDialog = () => {
    setShowNewListDialog(false);
    setNewListDefaultSettings({});
  };

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const onClearFailedList = async (list: List) => {
    // don't need to wait because the deletion can happen in the background
    void deleteList(list.id);

    setNewListDefaultSettings({
      name: list.name,
      type: list.type as OptOutListType,
      expiresAt: list.expiresAt
        ? DateTime.fromMillis(list.expiresAt)
        : undefined,
    });
    setShowNewListDialog(true);
  };

  const hasTriggeredInitialRefresh = useRef(false);
  useEffect(() => {
    if (hasTriggeredInitialRefresh.current) {
      // already triggered auto-refresh
      return;
    }

    const needsInitialRefresh =
      summary &&
      (summary.countsComputedAt === null ||
        // counts were last computed more than 4 hours ago
        new Date().getTime() - summary.countsComputedAt > 4 * 60 * 60 * 1000);
    if (needsInitialRefresh) {
      refreshCounts().catch(e => console.error(e));
      hasTriggeredInitialRefresh.current = true;
    }
  }, [hasTriggeredInitialRefresh, isGlobal, summary, refreshCounts]);

  const previousOptOutSharingEnabled = usePrevious(tenant.optOutSharingEnabled);
  useEffect(() => {
    if (tenant.optOutSharingEnabled !== previousOptOutSharingEnabled) {
      refreshCounts().catch(e => console.error(e));
    }
  }, [
    tenant.optOutSharingEnabled,
    previousOptOutSharingEnabled,
    refreshCounts,
  ]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const { optOutLists } = summary!;

  const [permanentLists, temporaryLists] = partition(
    optOutLists,
    list => list.type === "PERMANENT"
  );

  const title = isGlobal ? "Global Opt Outs" : "Opt Outs and Strike Lists";
  const description = isGlobal ? (
    <>
      Add lists of phone numbers to remove from future text campaigns by all
      subaccounts in this organization.
    </>
  ) : (
    <>
      Use this page to upload lists of phone numbers to remove from your texting
      campaigns. You can choose to upload a list of permanent Opt Outs, or a
      temporary &ldquo;Strike List&rdquo; of Opt Outs that expire after a
      certain time.
    </>
  );

  const renderOptOutCount = (count: number | null | undefined) => {
    if (refreshingCounts || count === null || count === undefined) {
      return (
        <FlexBox alignItems="center" gap="20px">
          <Typography variant="body3" sx={{ color: palette.darkGray }}>
            Calculating...
          </Typography>
          <Loading size={20} style={{ margin: 0 }} />
        </FlexBox>
      );
    }

    return (
      <FlexBox alignItems="center" gap="20px">
        <Typography variant="body3" sx={{ color: palette.darkGray }}>
          Last calculated {formatDateTime(summary!.countsComputedAt)}{" "}
          <Typography variant="body3Bold">
            <LinkButton
              onClick={refreshCounts}
              sx={{ color: palette.darkGray }}
            >
              Refresh
            </LinkButton>
          </Typography>
        </Typography>
        <Typography variant="display2Bold">{count.toLocaleString()}</Typography>
      </FlexBox>
    );
  };

  return (
    <PageContainer>
      <NewAdminList
        isOpen={showNewListDialog}
        onClose={hideNewListDialog}
        defaultSettings={newListDefaultSettings}
      />

      <FlexBox flexDirection="column" gap={theme.spacing(2)}>
        <Box>
          <PageHeader isSlim>{title}</PageHeader>
          <Typography variant="h2">{description}</Typography>
        </Box>

        <FlexBox
          justifyContent="space-between"
          alignItems="flex-end"
          gap={3}
          sx={{ marginBottom: 3 }}
        >
          <Box>
            <OptOutSharing isGlobal={isGlobal} />

            {subaccount && subaccountLink && subaccount.subscriberAudience ? (
              <Alert severity="warning" icon={false} sx={{ mt: 1 }}>
                <Typography variant="body1">
                  <strong>Warning:</strong> Opt outs on this page will not apply
                  to your short code subscribers. To opt out a number from short
                  code messaging you must unsubscribe them on the{" "}
                  <InternalLink to={subaccountLink("/audiences")}>
                    subscribers page.
                  </InternalLink>
                </Typography>
              </Alert>
            ) : null}
          </Box>

          <FlexBox gap={1}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ fontSize: 12 }}
                onClick={() => setSearchDialogOpen(true)}
              >
                <Search fontSize="small" />
                Search
              </Button>
            </Box>

            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ width: 120, fontSize: 12 }}
                onClick={() => setShowNewListDialog(true)}
              >
                <Add fontSize="small" /> New List
              </Button>
            </Box>
          </FlexBox>
        </FlexBox>
      </FlexBox>

      <FlexBox flexDirection="column" gap={theme.spacing(1)}>
        {subaccount && (
          <>
            <AccentBox>
              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold">Texter Opt Outs</Typography>
                {renderOptOutCount(
                  (summary! as SubaccountOptOutSummary).texterOptOutCount
                )}
              </FlexBox>
            </AccentBox>

            <AccentBox>
              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold">Auto Opt Outs</Typography>
                {renderOptOutCount(
                  (summary! as SubaccountOptOutSummary).autoOptOutCount
                )}
              </FlexBox>
            </AccentBox>
          </>
        )}

        <AccentBox sx={{ justifyContent: "unset", flexDirection: "column" }}>
          <FlexBox justifyContent="space-between">
            <Typography variant="h2Bold">Admin or External Opt Outs</Typography>
            <FlexBox gap={1} alignItems="center">
              {renderOptOutCount(summary!.permanentListOptOutCount)}
            </FlexBox>
          </FlexBox>
          <OptOutListTable
            subaccountId={subaccount?.id ?? null}
            type="PERMANENT"
            lists={permanentLists}
            onClearFailedList={onClearFailedList}
          />
        </AccentBox>

        {subaccount && (
          <>
            <AccentBox>
              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold">Temporary Opt Outs</Typography>
                <FlexBox gap={1} alignItems="center">
                  {renderOptOutCount(
                    (summary as any as SubaccountOptOutSummary)
                      .temporaryListOptOutCount
                  )}
                </FlexBox>
              </FlexBox>

              <OptOutListTable
                subaccountId={subaccount?.id ?? null}
                type="TEMPORARY"
                lists={temporaryLists}
                onClearFailedList={onClearFailedList}
              />
            </AccentBox>

            <AccentBox>
              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold">Global Opt Outs</Typography>
                {renderOptOutCount(
                  (summary as SubaccountOptOutSummary).globalOptOutCount
                )}
              </FlexBox>
            </AccentBox>

            {tenant.optOutSharingEnabled && (
              <AccentBox>
                <FlexBox justifyContent="space-between">
                  <Typography variant="h2Bold">
                    Shared Subaccount Opt Outs
                  </Typography>
                  {renderOptOutCount(
                    (summary as SubaccountOptOutSummary)
                      .sharedSubaccountOptOutCount
                  )}
                </FlexBox>
              </AccentBox>
            )}

            <AccentBox>
              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold">Spoke Synced Opt Outs</Typography>
                {renderOptOutCount(
                  (summary as SubaccountOptOutSummary).spokeOptOutCount
                )}
              </FlexBox>
            </AccentBox>

            <AccentBox>
              <Typography variant="h2Bold">Deleted Opt Out Lists</Typography>
              <DeletedOptOutListTable
                deleteOptOutListJobs={summary!.deleteOptOutListJobs}
              />
            </AccentBox>
          </>
        )}
      </FlexBox>

      <OptOutSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
      />
    </PageContainer>
  );
};
